import {
  AccountBalance,
  Fingerprint,
  Folder,
  Gavel,
  ReceiptLong,
  SvgIconComponent,
} from '@mui/icons-material';

import { IDocumentDirectory } from '../../Interfaces/IDocumentDirectory';

/**
 * Ordnerarten der internen Datenablage.
 *
 * - `standard`    klassischer Ordner: Dokumente werden manuell abgelegt,
 *                 verschoben oder entfernt.
 * - alle anderen  Systemordner (dynamische Ordner): sie besitzen eine feste
 *                 fachliche Funktion und spiegeln Dokumente aus anderen
 *                 Bereichen des Vorgangs. Ihr Inhalt wird berechnet und ist in
 *                 der Datenablage nicht veraenderbar.
 */
export type DocumentDirectoryType =
  | 'standard'
  | 'requirement'
  | 'certificate'
  | 'invoice'
  | 'gwg';

export const STANDARD_DIRECTORY_TYPE: DocumentDirectoryType = 'standard';

/**
 * Der GwG-Ordner ist der einzige Systemordner mit eigener Arbeitsoberfläche: er
 * spiegelt keine Dateien des Vorgangs, sondern zeigt die GwG-Unterlagen der
 * beteiligten juristischen Personen samt Anforderung, Erinnerungen und
 * Vollständigkeitskontrolle.
 */
export const GWG_DIRECTORY_TYPE: DocumentDirectoryType = 'gwg';

export interface IDocumentDirectoryTypeInfo {
  type: DocumentDirectoryType;
  label: string;
  description: string;
  defaultTitle: string;
  icon: SvgIconComponent;
  /** Systemordner ohne fertige Fachlogik – Inhalt wird spaeter ausgearbeitet. */
  isPreview?: boolean;
}

export const STANDARD_DIRECTORY_INFO: IDocumentDirectoryTypeInfo = {
  type: 'standard',
  label: 'Standardordner',
  description:
    'Klassischer Ordner. Dokumente können manuell abgelegt, verschoben oder entfernt werden.',
  defaultTitle: '',
  icon: Folder,
};

export const SYSTEM_DIRECTORY_INFO_ARRAY: IDocumentDirectoryTypeInfo[] = [
  {
    type: 'requirement',
    label: 'Maßnahmen',
    description:
      'Spiegelt die Dokumente aus den Maßnahmen des Vorgangs. Der Inhalt wird automatisch aktualisiert.',
    defaultTitle: 'Maßnahmen',
    icon: Gavel,
  },
  {
    type: 'certificate',
    label: 'Urkunden',
    description:
      'Zeigt die dem Vorgang zugeordneten Urkunden. Der Inhalt wird automatisch aktualisiert.',
    defaultTitle: 'Urkunden',
    icon: AccountBalance,
  },
  {
    type: 'invoice',
    label: 'Rechnungen',
    description:
      'Zeigt die dem Vorgang zugeordneten Rechnungen. Der Inhalt wird automatisch aktualisiert.',
    defaultTitle: 'Rechnungen',
    icon: ReceiptLong,
  },
  {
    type: 'gwg',
    label: 'GwG',
    description:
      'Prüft die geldwäscherechtlichen Unterlagen der beteiligten juristischen Personen: Pflicht- oder Optionalstatus je Dokumentart, Erinnerungen an die Systemgruppe GwG und Vollständigkeitskontrolle. Die Dokumente bleiben beim jeweiligen Mandanten.',
    defaultTitle: 'GwG',
    icon: Fingerprint,
  },
];

const DIRECTORY_INFO_MAP = new Map<
  DocumentDirectoryType,
  IDocumentDirectoryTypeInfo
>(
  [STANDARD_DIRECTORY_INFO, ...SYSTEM_DIRECTORY_INFO_ARRAY].map(info => [
    info.type,
    info,
  ])
);

/**
 * Ordnerart eines Datensatzes. Bestandsordner ohne gesetzte Art (bzw. mit einer
 * unbekannten Art aus einer neueren Version) gelten als Standardordner.
 */
export const getDirectoryType = (
  documentDirectory: IDocumentDirectory | undefined | null
): DocumentDirectoryType => {
  const rawType = documentDirectory?.DirectoryType;

  if (rawType && DIRECTORY_INFO_MAP.has(rawType as DocumentDirectoryType)) {
    return rawType as DocumentDirectoryType;
  }

  return STANDARD_DIRECTORY_TYPE;
};

export const getDirectoryTypeInfo = (
  type: DocumentDirectoryType
): IDocumentDirectoryTypeInfo =>
  DIRECTORY_INFO_MAP.get(type) ?? STANDARD_DIRECTORY_INFO;

export const isSystemDirectory = (
  documentDirectory: IDocumentDirectory | undefined | null
): boolean => getDirectoryType(documentDirectory) !== STANDARD_DIRECTORY_TYPE;
